import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { DeleteSystemAgentPackageRequest, DeployPackageToAllSystemsRequest, DeployPackageToAllSystemsResponse, DeployPackageToSystemsRequest, DeployPackageToSystemsResponse, GetSystemsWithAgentVersionsResponse, ListSystemAgentPackagesResponse, SystemAgentPackage, UploadSystemAgentPackageRequest } from '@features/deployment/domain/packages.types';
import { packageService } from './packageService';

export const packageKeys = {
  all: ['packages'] as const,
  lists: () => [...packageKeys.all, 'list'] as const,
  deployments: () => [...packageKeys.all, 'deployments'] as const,
  agentVersions: (systemIds: string[]) => [...packageKeys.deployments(), 'agent-versions', systemIds] as const,
};

export const packageListQueryOptions = () => {
  return queryOptions<ListSystemAgentPackagesResponse, AxiosError>({
    queryKey: packageKeys.lists(),
    queryFn: () => packageService.listPackages(),
  });
};

export const useUploadPackage = () => {
  const queryClient = useQueryClient();

  return useMutation<SystemAgentPackage, AxiosError, UploadSystemAgentPackageRequest, {
    uploadNotificationId: string
  }>({
    mutationFn: (request) => packageService.uploadPackage(request),
    onMutate: (variables) => {
      // Show upload started notification
      const uploadNotificationId = `upload-${Date.now()}`;
      notifications.show({
        id: uploadNotificationId,
        title: 'Uploading Package',
        message: `Uploading ${variables.version} (${(variables.file.size / 1024 / 1024).toFixed(1)} MB)...`,
        color: 'blue',
        loading: true,
        autoClose: false,
      });

      return {uploadNotificationId};
    },
    onSuccess: (data, _, context) => {
      // Invalidate the packages list to refetch after upload
      queryClient.invalidateQueries({queryKey: packageKeys.lists()});

      // Update the notification to show success
      if (context?.uploadNotificationId) {
        notifications.update({
          id: context.uploadNotificationId,
          title: 'Package Uploaded',
          message: `Successfully uploaded package ${data.version}`,
          color: 'green',
          loading: false,
          autoClose: 5000,
        });
      }
    },
    onError: (error: any, _, context) => {
      let errorMessage = 'Failed to upload package';

      // Extract validation errors from the API response
      if (error?.response?.data) {
        const responseData = error.response.data;

        // Handle validation errors with detailed messages
        if (responseData.errors && typeof responseData.errors === 'object') {
          const errorMessages = Object.entries(responseData.errors)
            .flatMap(([, messages]) => messages as string[]);
          errorMessage = errorMessages.join(', ');
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else if (responseData.title) {
          errorMessage = responseData.title;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Update the existing notification or create a new one
      if (context?.uploadNotificationId) {
        notifications.update({
          id: context.uploadNotificationId,
          title: 'Upload Failed',
          message: errorMessage,
          color: 'red',
          loading: false,
          autoClose: 10000,
        });
      } else {
        notifications.show({
          title: 'Upload Failed',
          message: errorMessage,
          color: 'red',
        });
      }
    },
  });
};

export const useDeletePackage = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, DeleteSystemAgentPackageRequest & { packageVersion?: string }>({
    mutationFn: (request) => packageService.deletePackage(request),
    onSuccess: (_, variables) => {
      // Invalidate the packages list to refetch after delete
      queryClient.invalidateQueries({queryKey: packageKeys.lists()});

      notifications.show({
        title: 'Package Deleted',
        message: `Successfully deleted package${variables.packageVersion ? ` ${variables.packageVersion}` : ''}`,
        color: 'green',
      });
    },
  });
};

export const systemsWithAgentVersionsQueryOptions = (systemIds: string[]) => {
  return queryOptions<GetSystemsWithAgentVersionsResponse, AxiosError>({
    queryKey: packageKeys.agentVersions(systemIds),
    queryFn: () => packageService.getSystemsWithAgentVersions({systemIds}),
    enabled: systemIds.length > 0,
  });
};

export const useDeployPackageToSystems = () => {
  return useMutation<DeployPackageToSystemsResponse, AxiosError, DeployPackageToSystemsRequest>({
    mutationFn: (request) => packageService.deployPackageToSystems(request),
    onSuccess: (data) => {
      notifications.show({
        title: 'Deployment Started',
        message: `Job ID: ${data.jobId}`,
        color: 'green',
      });
    },
  });
};

export const useDeployPackageToAllSystems = () => {
  return useMutation<DeployPackageToAllSystemsResponse, AxiosError, DeployPackageToAllSystemsRequest>({
    mutationFn: (request) => packageService.deployPackageToAllSystems(request),
    onSuccess: (data) => {
      notifications.show({
        title: 'Mass Deployment Started',
        message: `Deploying to ${data.totalSystemsCount} systems. Job ID: ${data.primaryJobId}`,
        color: 'green',
        autoClose: 10000,
      });
    },
  });
};